// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";

import { DashboardPanel } from "types";
import { IAction, ActionType, raiseAction, IActionTypeSelectGuiItem } from "actions";
import { ITariffCircuit } from "parameter";
import { isSet } from "utils";
import { IState } from "states/states";
import { PowerViewMode } from "states/cachedstates";
import { getPanelSelection } from "guiutils";

import { ReactComponent as SvgHierarchyChart } from "static/iconHierarcyChart.svg";
import { ReactComponent as SvgSankeyChart } from "static/iconSankeyChart.svg";
import { ReactComponent as SvgPieChart } from "static/iconPieChart.svg";
import { ReactComponent as SvgListView } from "static/iconListView.svg";
import EnergySourceSelect from "./EnergySourceSelect";
import HierarchySelect from "./HierarchySelect";
import TrendMemberSelect from "view/comp/TrendMemberSelect";
import SearchFilter from "./SearchFilter";
import TariffCircuitSelect from "./TariffCircuitSelect";
import CategorySelect from "./CategorySelect";

interface IPowerHeadingExtProps {
  viewModes: PowerViewMode[];
  panel: DashboardPanel | undefined;
  dashboardIndex?: number;
  filterText: string;
  onFilterTextChanged: (filterText: string) => void;
}

interface IPowerHeadingProps {
  powerViewMode: PowerViewMode;
  selectedDashboardIndex: number;
  selectedHierarchyId: number | null | undefined;
  tariffCircuits: ITariffCircuit[];
}

const mapStateToProps = (state: IState, ownProps: IPowerHeadingExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    ownProps.panel ?? DashboardPanel.POWER,
    ownProps.dashboardIndex,
  );
  const props: IPowerHeadingProps = {
    powerViewMode: selection?.powerViewMode ?? PowerViewMode.TABLE,
    selectedDashboardIndex: index,
    selectedHierarchyId: selection?.hierarchyId,
    tariffCircuits: state.parameter?.tariffline ?? [],
  };
  return props;
};

interface IPowerHeadingDispatch {
  onSwitchPowerView: (dashboardIndex: number, selection: PowerViewMode) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IPowerHeadingDispatch = {
    onSwitchPowerView: (dashboardIndex: number, selection: PowerViewMode) => {
      const payload: IActionTypeSelectGuiItem = { dashboardIndex, selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_POWERVIEWMODE, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  switchToHierarchy: {
    id: "switch to hierarchy view",
  },
  switchtoSankey: {
    id: "switch to sankey view",
  },
  switchToPie: {
    id: "switch to pie-chart view",
  },
  switchToList: {
    id: "switch to list view",
  },
  placeholderSearchFiled: {
    id: "highlight tariffs circuits...",
  },
});

class PowerHeadingComponent extends React.Component<
  IPowerHeadingProps & IPowerHeadingExtProps & IPowerHeadingDispatch & WrappedComponentProps
> {
  public render() {
    // in pie-chart view mode, we must not switch to all energy sources
    const allowAllSources = this.props.powerViewMode !== PowerViewMode.PIE;

    return (
      <div className="headerbuttons mb-2">
        <div className="left">
          <EnergySourceSelect
            className="me-2"
            panel={this.props.panel ?? DashboardPanel.POWER}
            allowAllEnergySources={allowAllSources}
          />
          <HierarchySelect
            className="me-2"
            panel={this.props.panel ?? DashboardPanel.POWER}
            allowAllEnergySources={allowAllSources}
            allowAllHierarchies={true}
          />
          <CategorySelect
            className="me-2"
            panel={this.props.panel ?? DashboardPanel.POWER}
            allowAllEnergySources={allowAllSources}
            allowAllHierarchies={true}
            includeChildren={true}
          />
          {this.props.selectedHierarchyId === undefined && (
            <TariffCircuitSelect
              className="me-2"
              panel={this.props.panel ?? DashboardPanel.POWER}
              allowAllEnergySources={allowAllSources}
              allowAllHierarchies={true}
              ignoreCategories={false}
            />
          )}
          {(this.props.panel ?? DashboardPanel.POWER) !== DashboardPanel.POWER && (
            <TrendMemberSelect
              showOnlyPower={(this.props.panel ?? DashboardPanel.POWER) === DashboardPanel.POWER}
            />
          )}
          <SearchFilter
            className="tableSearchBox"
            filterText={this.props.filterText ?? ""}
            placeholder={this.props.intl.formatMessage(messages.placeholderSearchFiled)}
            onChange={(filterText) => this.props.onFilterTextChanged(filterText)}
            autoFocus={false}
          />
        </div>
        {this.renderViewModes()}
      </div>
    );
  }

  private renderViewModes() {
    // const hasCategories = this.props.tariffCircuits.some(
    //   (tariff) => isSet(tariff.categories) && tariff.categories.some((cat) => cat !== ""),
    // );
    const hasHierarchies = this.props.tariffCircuits.some((tariff) => isSet(tariff.parent));
    const showSankeyAndHiearchy = hasHierarchies;

    return (
      this.props.viewModes.length > 1 && (
        <div id="graphswitch">
          {this.props.viewModes.includes(PowerViewMode.HIERARCY) === true &&
            showSankeyAndHiearchy && (
              <Button
                variant="secondary"
                className={
                  this.props.powerViewMode === PowerViewMode.HIERARCY ? "active" : undefined
                }
                title={this.props.intl.formatMessage(messages.switchToHierarchy)}
                onClick={this.props.onSwitchPowerView.bind(
                  this,
                  this.props.selectedDashboardIndex,
                  PowerViewMode.HIERARCY,
                )}
              >
                <SvgHierarchyChart />
              </Button>
            )}
          {this.props.viewModes.includes(PowerViewMode.SANKEY) === true &&
            showSankeyAndHiearchy && (
              <Button
                variant="secondary"
                className={this.props.powerViewMode === PowerViewMode.SANKEY ? "active" : undefined}
                title={this.props.intl.formatMessage(messages.switchtoSankey)}
                onClick={this.props.onSwitchPowerView.bind(
                  this,
                  this.props.selectedDashboardIndex,
                  PowerViewMode.SANKEY,
                )}
              >
                <SvgSankeyChart />
              </Button>
            )}
          {this.props.viewModes.includes(PowerViewMode.PIE) === true && (
            <Button
              variant="secondary"
              className={this.props.powerViewMode === PowerViewMode.PIE ? "active" : undefined}
              title={this.props.intl.formatMessage(messages.switchToPie)}
              onClick={this.props.onSwitchPowerView.bind(
                this,
                this.props.selectedDashboardIndex,
                PowerViewMode.PIE,
              )}
            >
              <SvgPieChart />
            </Button>
          )}
          {this.props.viewModes.includes(PowerViewMode.TABLE) === true && (
            <Button
              variant="secondary"
              className={this.props.powerViewMode === PowerViewMode.TABLE ? "active" : undefined}
              title={this.props.intl.formatMessage(messages.switchToList)}
              onClick={this.props.onSwitchPowerView.bind(
                this,
                this.props.selectedDashboardIndex,
                PowerViewMode.TABLE,
              )}
            >
              <SvgListView />
            </Button>
          )}
        </div>
      )
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(PowerHeadingComponent));
